import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputBase,
    Pagination,
    CircularProgress,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import apiUrl from '../../Config';
import { useNavigate } from 'react-router-dom';

const DisplayBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Pagination and Filters
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const [filterStatus, setFilterStatus] = useState('');
    const [filterBookingId, setFilterBookingId] = useState('');

    // Edit Dialog State
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentBooking, setCurrentBooking] = useState(null);

    // Edit fields
    const [newStatus, setNewStatus] = useState('');
    const [deliveredVia, setDeliveredVia] = useState('');
    const [trackId, setTrackId] = useState('');
    const [reciptFile, setReciptFile] = useState(null);
    const [preview, setPreview] = useState(null);

    const navigate = useNavigate();

    const statusMap = {
        '': 'All Status',
        pending: 'Pending',
        orderConfirmed: 'Order Confirmed',
        deliveryTransit: 'Delivery Transit',
        orderDelivered: 'Order Delivered',
        cancelled: 'Cancelled'
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setReciptFile(file);

        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setPreview(objectUrl);
        } else {
            setPreview(null);
        }
    };

    const fetchBookings = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(`${apiUrl}/bookings/getallbookings`, {
                page,
                limit,
                searchTerm: filterBookingId.trim(),
                bookingStatus: filterStatus
            });
            if (response.data.status) {
                setBookings(response.data.bookings || []);

                setTotalPages(response.data.totalPages || 1);
                setTotalItems(response.data.totalItems || 0);
            } else {
                setBookings([]);
                setTotalPages(1);
                setTotalItems(0);
            }
        } catch (err) {

            setError(err?.response?.data?.message || err.message || 'Failed to fetch bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
    }, [page, limit, filterStatus, filterBookingId]);

    const handleBookingIdKeyDown = (e) => {
        if (e.key === 'Enter') {
            setPage(1);
            fetchBookings();
        }
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setPage(1);
    };

    const handleEditClick = (booking) => {
        setCurrentBooking(booking);
        setNewStatus(booking.bookingStatus || '');
        setDeliveredVia(booking.deliveredvia || '');
        setTrackId(booking.trackId || '');

        // Set existing receipt image as string
        setReciptFile(booking.reciptUrl); // Since it's a URL, not a File object
        setPreview(booking.reciptUrl || null); // Set preview directly
        setEditDialogOpen(true);
    };


    const handleSaveClick = async () => {
        if (!currentBooking) return;
        setLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('bookingStatus', newStatus);
            if (deliveredVia) {
                formData.append('deliveredvia', deliveredVia);
            }

            if (trackId) {
                formData.append('trackId', trackId);
            }

            if (reciptFile instanceof File) {
                formData.append('reciptUrl', reciptFile);
            }

            await axios.put(`${apiUrl}/bookings/updatebooking/${currentBooking.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setEditDialogOpen(false);
            setCurrentBooking(null);
            fetchBookings();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to update booking');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={3} mt={2}>
            <Typography variant="h4" gutterBottom>
                Bookings
            </Typography>

            {/* Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'center' }}>
                    <InputBase
                        placeholder="Search by Booking ID"
                        sx={{ border: '1px solid #ccc', px: 2, py: 0.5, borderRadius: 1, width: 200 }}
                        value={filterBookingId}
                        onChange={(e) => setFilterBookingId(e.target.value)}
                        onKeyDown={handleBookingIdKeyDown}
                    />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={filterStatus}
                            onChange={(e) => {
                                setFilterStatus(e.target.value);
                                setPage(1);
                            }}
                            displayEmpty
                        >
                            {Object.entries(statusMap).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'end', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
                        <Select
                            labelId="rows-per-page-label"
                            value={limit}
                            onChange={handleLimitChange}
                            label="Rows per page"
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Booking ID</TableCell>
                                    <TableCell>User Name</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Total Amount</TableCell>
                                    <TableCell>Booking Date</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bookings.length > 0 ? (
                                    bookings.map((booking) => (
                                        <TableRow key={booking.id}>
                                            <TableCell>{booking.id}</TableCell>
                                            <TableCell>{booking.user?.name}</TableCell>
                                            <TableCell>{booking.user?.phoneNumber}</TableCell>
                                            <TableCell>{booking.user?.email}</TableCell>
                                            <TableCell>{booking.bookingStatus}</TableCell>
                                            <TableCell>₹{booking.totalAmount}</TableCell>
                                            <TableCell>{new Date(booking.bookingDate).toLocaleString()}</TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleEditClick(booking)} title="Edit">
                                                    <EditIcon />
                                                </IconButton>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => navigate(`/booking-details/${booking.id}`)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    View More
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No bookings found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            showFirstButton
                            showLastButton
                            siblingCount={0}
                            boundaryCount={1}
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    height: 30,
                                    minWidth: 30,
                                    fontSize: '1rem',
                                    marginTop: '10px'
                                }
                            }}
                        />
                    </Box>
                </>
            )}

            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {/* Edit Booking Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Booking</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={newStatus}
                            label="Status"
                            onChange={(e) => setNewStatus(e.target.value)}
                        >
                            {Object.entries(statusMap).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Show these fields only if newStatus is "Delivery Transit" */}
                    {newStatus === "deliveryTransit" && (
                        <>
                            <TextField
                                label="Delivered Via"
                                fullWidth
                                size="small"
                                value={deliveredVia}
                                onChange={(e) => setDeliveredVia(e.target.value)}
                            />
                            <TextField
                                label="Tracking ID"
                                fullWidth
                                size="small"
                                value={trackId}
                                onChange={(e) => setTrackId(e.target.value)}
                            />
                            <Button variant="outlined" component="label">
                                Upload Receipt
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                            </Button>

                            {preview && (
                                <img
                                    src={preview}
                                    alt="Receipt Preview"
                                    style={{ marginTop: 8, maxWidth: 80, maxHeight: 80, borderRadius: 4 }}
                                />
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button variant='outlined' onClick={() => setEditDialogOpen(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveClick}
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default DisplayBookings;
